// ./react-app/src/components/NewsletterStatisticsDual.tsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { visuallyHidden } from '@mui/utils';
import fetchData from '../helpers/fetchData';
import { FilterContext, IFilterState } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import LoadingSpinner from './LoadingSpinner';
import { formatNumber } from '../helpers/numberUtils';
import NewsletterGroupRatesChart from './NewsletterGroupRatesChart';
import { getComparator, Order, sortedRowInformation } from '../helpers/sortingsFuncs';
import { exportToExcel } from '../helpers/exportFuncs';

type INewsletterStatisticsResponse = Record<string, string> & {
  campaign_name?: string;
  send_date?: string;
};
type DataKey = keyof INewsletterStatisticsResponse;
type Data = Record<DataKey, string>;

// The header mapping
const headerMapping: Record<string, string> = {
  newsletter_title: 'Name',
  esp_source: 'ESP',
  total_sends: 'Sends',
  total_recipients: 'Empfänger',
  avg_recipients: 'Ø Empfänger',
  total_unique_opens: 'Unique Öffner',
  avg_unique_opens: 'Ø Unique Öffner',
  avg_open_rate_unique: 'Ø Öffnungsrate (Unique)',
  //total_clicks: "Klicks",
  //total_unique_clicks: "Unique Klicks",
  //avg_clicks: "Ø Klicks",
  //avg_click_rate: "Ø Klickrate",
  //avg_unique_clicks: "Ø Unique Klicks",
  unsubscribed: 'Abmelder',
  avg_unsubscribed: 'Ø Abmelder',
  unsub_rate: 'Abmelderate (Unique Öffner)',
  //avg_soft_bounces: "Ø Soft Bounces",
  //avg_hard_bounces: "Ø Hard Bounces",
  //website_traffic: "Website Traffic",
  ad_clicks: 'Ad-Klicks',
  avg_ad_clicks: 'Ø Ad-Klicks',
  ad_click_rate: 'Ad-Klickrate (Unique Öffner)',
  ad_click_rate_recipients: 'Ad-Klickrate (Empfänger)',
  total_leads: 'Leads',
  avg_leads: 'Ø Leads',
  lead_rate: 'Leadrate (Unique Öffner)',
  total_turnover: 'Umsatz',
  avg_turnover: 'Ø Umsatz',
  etkp: 'ETKP',
  etko: 'ETKO',
};

const headers = Object.keys(headerMapping);

type ESPFilter = 'all' | 'mailchimp' | 'iterable';
type CampaignTypeFilter = 'all' | 'newsletter' | 'automated_sam' | 'manual';

const getAndProcessData = async (filterState: IFilterState) => {
  if (!filterState.startDate) {
    throw new Error('No start date provided');
  }
  const options = {
    start_date: dateToSqlDate(filterState.startDate),
    end_date: filterState.endDate ? dateToSqlDate(filterState.endDate) : '',
    sam_group_by: filterState.samGrouping,
    customer_id: filterState.customers,
  };

  return await fetchData<INewsletterStatisticsResponse>(
    '/api/newsletter-aggregated-statistics-dual',
    options,
  );
};

const NewsletterStatisticsDual: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<DataKey>('campaign_name');
  const [data, setData] = useState<INewsletterStatisticsResponse[]>([]);
  const [filteredData, setFilteredData] = useState<INewsletterStatisticsResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [espFilter, setEspFilter] = useState<ESPFilter>('all');
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<CampaignTypeFilter>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [detailsData, setDetailsData] = useState<INewsletterStatisticsResponse[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [selectedChartGroup, setSelectedChartGroup] = useState<string>('');

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    setLoading(true);
    setData([]); // Clear the data state when filterState changes
    const fetchData = async () => {
      try {
        const rawData = await getAndProcessData(filterState);
        const dataArray = Array.isArray(rawData) ? rawData : [rawData];

        // Convert numeric string fields to actual numbers for proper sorting
        const processedData = dataArray.map((row) => {
          const processed = { ...row };
          // Convert numeric fields that come as strings from API
          const numericFields = [
            'total_sends',
            'total_recipients',
            'avg_recipients',
            'total_unique_opens',
            'avg_unique_opens',
            'total_unique_clicks',
            'avg_clicks',
            'unsubscribed',
            'avg_unsubscribed',
            'ad_clicks',
            'avg_ad_clicks',
            'total_leads',
            'avg_leads',
            'total_turnover',
            'avg_turnover',
            'avg_open_rate_unique',
            'avg_click_rate',
            'unsub_rate',
            'ad_click_rate',
            'ad_click_rate_recipients',
            'lead_rate',
            'etkp',
            'etko',
          ];

          numericFields.forEach((field) => {
            if (
              processed[field] !== null &&
              processed[field] !== undefined &&
              processed[field] !== ''
            ) {
              const numValue = parseFloat(processed[field] as string);
              if (!isNaN(numValue)) {
                (processed as any)[field] = numValue;
              }
            }
          });

          return processed;
        });

        setData(processedData);
        setLoading(false);
      } catch (err: any) {
        setError(err);
      }
    };

    fetchData();
  }, [filterState]);

  // Filter data based on ESP selection and campaign type
  useEffect(() => {
    let filtered = [...data];

    // Apply ESP filter
    if (espFilter !== 'all') {
      filtered = filtered.filter((row) => row.esp_source === espFilter);
    }

    // Apply campaign type filter
    if (campaignTypeFilter !== 'all') {
      filtered = filtered.filter((row) => {
        const title = row.newsletter_title;

        switch (campaignTypeFilter) {
          case 'newsletter':
            return (
              title.startsWith('Newsletter - ') ||
              title.includes('Daily Bulletin') ||
              title.includes('Morning Briefing') ||
              title.includes('Crypto Briefing') ||
              title.includes('Die Aktie des Tages') ||
              title.includes('Das Newsbriefing') ||
              title.includes('Das Wasserstoff Briefing') ||
              title.includes('Aktien unter der Chartlupe') ||
              (title.includes('[') && title.includes(']'))
            ); // Legacy newsletter patterns

          case 'automated_sam':
            return (
              title.startsWith('Automatisierte SAM') || title.startsWith('Automatisierte Kampagnen')
            );

          case 'manual':
            return title.startsWith('Manuelle Kampagnen');

          default:
            return true;
        }
      });
    }

    setFilteredData(filtered);
  }, [data, espFilter, campaignTypeFilter]);

  const createSortHandler = (property: keyof Data) => () => {
    handleRequestSort(property);
  };

  const handleESPFilterChange = (event: any) => {
    setEspFilter(event.target.value as ESPFilter);
  };

  const handleCampaignTypeFilterChange = (event: any) => {
    setCampaignTypeFilter(event.target.value as CampaignTypeFilter);
  };

  const handleRowClick = async (row: INewsletterStatisticsResponse) => {
    // Don't open dialog for individual campaigns that aren't grouped
    const groupedNames = [
      'Newsletter - Werktage - Rendite Komitee',
      'Newsletter - Wochenende - Trading Echo',
      'Newsletter - Wochenende - Money Trail',
      'Manuelle Kampagnen - Iterable',
      'Manuelle Kampagnen - Mailchimp',
      'Automatisierte SAM - Dup Send',
      'Automatisierte SAM - Main Send',
      'Automatisierte SAM - Non Opener Send',
      'Automatisierte Kampagnen Iterable - Dup Send',
      'Automatisierte Kampagnen Iterable - Main Send',
      'Automatisierte Kampagnen Iterable - Non Opener Send',
    ];

    // Check if the row title matches any of the fixed group names or patterns
    const isGrouped =
      groupedNames.includes(row.newsletter_title) ||
      row.newsletter_title.startsWith('Automatisierte SAM - Kunde: ') ||
      row.newsletter_title.startsWith('Automatisierte Kampagnen Iterable - Kunde: ');

    if (!isGrouped) {
      return;
    }

    setSelectedGroup(row.newsletter_title);
    setDialogOpen(true);
    setDetailsLoading(true);

    try {
      if (!filterState.startDate) {
        console.error('No start date provided');
        setDetailsLoading(false);
        return;
      }

      const options = {
        start_date: dateToSqlDate(filterState.startDate),
        end_date: filterState.endDate ? dateToSqlDate(filterState.endDate) : '',
        group_name: row.newsletter_title,
        esp_source: row.esp_source,
        customer_id: filterState.customers,
      };

      const details = await fetchData<INewsletterStatisticsResponse>(
        '/api/newsletter-campaign-details',
        options,
      );

      setDetailsData(Array.isArray(details) ? details : []);
      setDetailsLoading(false);
    } catch (err) {
      console.error('Error fetching campaign details:', err);
      setDetailsData([]);
      setDetailsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGroup('');
    setDetailsData([]);
  };

  const handleChartClick = (row: INewsletterStatisticsResponse, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click handler

    // Same logic as row click to determine if it's a grouped row
    const groupedNames = [
      'Newsletter - Werktage - Rendite Komitee',
      'Newsletter - Wochenende - Trading Echo',
      'Newsletter - Wochenende - Money Trail',
      'Manuelle Kampagnen - Iterable',
      'Manuelle Kampagnen - Mailchimp',
      'Automatisierte SAM - Dup Send',
      'Automatisierte SAM - Main Send',
      'Automatisierte SAM - Non Opener Send',
      'Automatisierte Kampagnen Iterable - Dup Send',
      'Automatisierte Kampagnen Iterable - Main Send',
      'Automatisierte Kampagnen Iterable - Non Opener Send',
    ];

    const isGrouped =
      groupedNames.includes(row.newsletter_title) ||
      row.newsletter_title.startsWith('Automatisierte SAM - Kunde: ') ||
      row.newsletter_title.startsWith('Automatisierte Kampagnen Iterable - Kunde: ');

    if (!isGrouped) {
      return;
    }

    setSelectedChartGroup(row.newsletter_title);
    setChartDialogOpen(true);
  };

  const handleCloseChartDialog = () => {
    setChartDialogOpen(false);
    setSelectedChartGroup('');
  };

  // Helper function to check if a row is grouped
  const isGroupedRow = (title: string) => {
    const groupedNames = [
      'Newsletter - Werktage - Rendite Komitee',
      'Newsletter - Wochenende - Trading Echo',
      'Newsletter - Wochenende - Money Trail',
      'Manuelle Kampagnen - Iterable',
      'Manuelle Kampagnen - Mailchimp',
      'Automatisierte SAM - Dup Send',
      'Automatisierte SAM - Main Send',
      'Automatisierte SAM - Non Opener Send',
      'Automatisierte Kampagnen Iterable - Dup Send',
      'Automatisierte Kampagnen Iterable - Main Send',
      'Automatisierte Kampagnen Iterable - Non Opener Send',
    ];

    return (
      groupedNames.includes(title) ||
      title.startsWith('Automatisierte SAM - Kunde: ') ||
      title.startsWith('Automatisierte Kampagnen Iterable - Kunde: ')
    );
  };

  // Calculate summary row for filtered data
  const calculateSummary = () => {
    if (filteredData.length === 0) return null;

    const summary: any = {
      newsletter_title: 'GESAMT',
      esp_source: espFilter === 'all' ? 'Beide' : espFilter,
      total_sends: 0,
      total_recipients: 0,
      total_unique_opens: 0,
      unsubscribed: 0,
      ad_clicks: 0,
      total_leads: 0,
      total_turnover: 0,
    };

    filteredData.forEach((row) => {
      summary.total_sends += parseInt(row.total_sends || '0', 10) || 0;
      summary.total_recipients += parseInt(row.total_recipients || '0', 10) || 0;
      summary.total_unique_opens += parseInt(row.total_unique_opens || '0', 10) || 0;
      summary.unsubscribed += parseInt(row.unsubscribed || '0', 10) || 0;
      summary.ad_clicks += parseInt(row.ad_clicks || '0', 10) || 0;
      summary.total_leads += parseInt(row.total_leads || '0', 10) || 0;
      summary.total_turnover += parseFloat(row.total_turnover || '0') || 0;
    });

    // Calculate averages and rates
    if (summary.total_sends > 0) {
      summary.avg_recipients = Math.round(summary.total_recipients / summary.total_sends);
      summary.avg_unique_opens = Math.round(summary.total_unique_opens / summary.total_sends);
      summary.avg_unsubscribed = Math.round(summary.unsubscribed / summary.total_sends);
      summary.avg_ad_clicks = Math.round(summary.ad_clicks / summary.total_sends);
      summary.avg_leads = Math.round(summary.total_leads / summary.total_sends);
      summary.avg_turnover = Math.round(summary.total_turnover / summary.total_sends);
    }

    if (summary.total_recipients > 0) {
      summary.avg_open_rate_unique = (summary.total_unique_opens / summary.total_recipients) * 100;
      summary.ad_click_rate_recipients = (summary.ad_clicks / summary.total_recipients) * 100;
      summary.etkp = (summary.total_turnover / summary.total_recipients) * 1000;
    }

    if (summary.total_unique_opens > 0) {
      summary.unsub_rate = (summary.unsubscribed / summary.total_unique_opens) * 100;
      summary.ad_click_rate = (summary.ad_clicks / summary.total_unique_opens) * 100;
      summary.lead_rate = (summary.total_leads / summary.total_unique_opens) * 100;
      summary.etko = (summary.total_turnover / summary.total_unique_opens) * 1000;
    }

    return summary;
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const summaryRow = calculateSummary();

  return loading ? (
    <LoadingSpinner />
  ) : (
    <Box bgcolor="#fff">
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Box display="flex" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="campaign-type-filter-label">Kampagnentyp</InputLabel>
            <Select
              labelId="campaign-type-filter-label"
              id="campaign-type-filter"
              value={campaignTypeFilter}
              label="Kampagnentyp"
              onChange={handleCampaignTypeFilterChange}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="newsletter">Newsletter</MenuItem>
              <MenuItem value="automated_sam">Automatisierte SAMs</MenuItem>
              <MenuItem value="manual">Manuelle Kampagnen</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="esp-filter-label">ESP Filter</InputLabel>
            <Select
              labelId="esp-filter-label"
              id="esp-filter"
              value={espFilter}
              label="ESP Filter"
              onChange={handleESPFilterChange}
            >
              <MenuItem value="all">Alle ESPs</MenuItem>
              <MenuItem value="mailchimp">Nur Mailchimp</MenuItem>
              <MenuItem value="iterable">Nur Iterable</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button onClick={() => exportToExcel(filteredData, 'Newsletter-Auswertung-Dual')}>
          Download als Excel
        </Button>
      </Box>
      <TableContainer
        component={Paper}
        key={JSON.stringify(filterState)} //force rerender on filter change
      >
        <Table sx={{ minWidth: 650 }} aria-label="Aggregierte Statistik">
          <TableHead>
            <TableRow>
              <TableCell />
              {headers.map((header) => (
                <TableCell key={header}>
                  <TableSortLabel
                    active={orderBy === header}
                    direction={orderBy === header ? order : 'asc'}
                    onClick={createSortHandler(header)}
                  >
                    {headerMapping[header] || header}
                    {orderBy === header ? (
                      <Box component="span" sx={{ ...visuallyHidden }}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRowInformation<Data>(filteredData, getComparator(order, orderBy)).map(
              (row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                  onClick={() => handleRowClick(row)}
                >
                  <TableCell sx={{ padding: '6px' }}>
                    {isGroupedRow(row.newsletter_title) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleChartClick(row, e)}
                        title="Ratenverlauf anzeigen"
                      >
                        <ShowChartIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  {Object.entries(row).map(([key, value]) => {
                    // Check if the key is present in headerMapping -> as click parameters should be skipped
                    if (!headerMapping.hasOwnProperty(key) && key !== 'name') {
                      return null;
                    }
                    return (
                      <TableCell key={key}>
                        {key === 'esp_source' ? (
                          <Chip
                            label={value}
                            size="small"
                            color={value === 'iterable' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        ) : Number(value) ? (
                          formatNumber(
                            Number(value),
                            [
                              'avg_open_rate_unique',
                              'avg_click_rate',
                              'avg_hard_bounces',
                              'unsub_rate',
                              'etkp',
                              'etko',
                            ].includes(key)
                              ? 2
                              : ['ad_click_rate', 'lead_rate', 'ad_click_rate_recipients'].includes(
                                    key,
                                  )
                                ? 3
                                : 0,
                          ) +
                          ([
                            'avg_open_rate_unique',
                            'avg_click_rate',
                            'ad_click_rate',
                            'ad_click_rate_recipients',
                            'lead_rate',
                            'unsub_rate',
                          ].includes(key)
                            ? ' %'
                            : ['total_turnover', 'avg_turnover', 'etkp', 'etko'].includes(key)
                              ? ' €'
                              : '')
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ),
            )}
            {/* Summary Row */}
            {summaryRow && (
              <TableRow
                sx={{
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  '& td': {
                    fontWeight: 'bold',
                    borderTop: '2px solid #333',
                  },
                }}
              >
                <TableCell />
                {headers.map((key) => {
                  const value = summaryRow[key];
                  if (value === undefined) {
                    return null;
                  }
                  return (
                    <TableCell key={key}>
                      {key === 'esp_source' ? (
                        <Chip label={String(value)} size="small" color="default" variant="filled" />
                      ) : typeof value === 'number' ? (
                        formatNumber(
                          value,
                          [
                            'avg_open_rate_unique',
                            'avg_click_rate',
                            'avg_hard_bounces',
                            'unsub_rate',
                            'etkp',
                            'etko',
                          ].includes(key)
                            ? 2
                            : ['ad_click_rate', 'lead_rate', 'ad_click_rate_recipients'].includes(
                                  key,
                                )
                              ? 3
                              : 0,
                        ) +
                        ([
                          'avg_open_rate_unique',
                          'avg_click_rate',
                          'ad_click_rate',
                          'ad_click_rate_recipients',
                          'lead_rate',
                          'unsub_rate',
                        ].includes(key)
                          ? ' %'
                          : ['total_turnover', 'avg_turnover', 'etkp', 'etko'].includes(key)
                            ? ' €'
                            : '')
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {selectedGroup} - Einzelne Kampagnen
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <LoadingSpinner />
          ) : detailsData.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kampagne</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell align="right">Empfänger</TableCell>
                    <TableCell align="right">Öffner</TableCell>
                    <TableCell align="right">Öffnungsrate</TableCell>
                    <TableCell align="right">Ad-Klicks</TableCell>
                    <TableCell align="right">Leads</TableCell>
                    <TableCell align="right">Umsatz</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsData.map((campaign, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{campaign.campaign_name}</TableCell>
                      <TableCell>
                        {campaign.send_date
                          ? new Date(campaign.send_date).toLocaleDateString('de-DE')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.total_recipients), 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.total_unique_opens), 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.avg_open_rate_unique), 2)} %
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.ad_clicks), 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.total_leads), 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(Number(campaign.total_turnover), 2)} €
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
              Diese Ansicht zeigt einzelne Kampagnen innerhalb der ausgewählten Gruppe. Die
              Implementierung folgt in Kürze.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {detailsData.length > 0 && (
            <Button onClick={() => exportToExcel(detailsData, `${selectedGroup}-Details`)}>
              Excel Export
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Chart Dialog */}
      <Dialog open={chartDialogOpen} onClose={handleCloseChartDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {selectedChartGroup} - Ratenverlauf
            </Typography>
            {selectedChartGroup.startsWith('Newsletter - ') &&
              !selectedChartGroup.includes('Mailchimp') &&
              !selectedChartGroup.includes('Iterable') && (
                <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                  (Nur Iterable-Daten)
                </Typography>
              )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseChartDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {filterState.startDate && filterState.endDate && (
            <NewsletterGroupRatesChart
              groupName={selectedChartGroup}
              startDate={filterState.startDate}
              endDate={filterState.endDate}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChartDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsletterStatisticsDual;
